import { ChunkEntry } from '@atlassian/bitkit-diff';

import { ContextDatum } from '../expand-context-saga';

const NUM_CONTEXT_LINES = 10;
// line ranges are inclusive
export const CONTEXT_LINES_RANGE = 9;
export const MULTI_CONTEXT_LINES_RANGE = 500;

export function buildLineNumbers(length: number, startingFrom: number) {
  return Array.from({ length }, (_, k) => k + startingFrom);
}

function chunksWillConnect(
  chunks: ChunkEntry[],
  chunkIndex: number,
  shouldExpandDown: boolean
) {
  const chunk = chunks[chunkIndex];
  const isFirstChunk = chunkIndex === 0;
  const isLastChunk = chunkIndex >= chunks.length - 1;

  if (shouldExpandDown) {
    if (isLastChunk) {
      return false;
    }
    const contextLinesBottom =
      chunk.oldStart + chunk.oldLines + NUM_CONTEXT_LINES;
    const nextChunk = chunks[chunkIndex + 1] || {};
    const nextChunkTop = nextChunk.oldStart;
    return contextLinesBottom >= nextChunkTop;
  } else {
    if (isFirstChunk) {
      return false;
    }
    const contextLinesTop = chunk.oldStart - NUM_CONTEXT_LINES;
    const nextChunk = chunks[chunkIndex - 1] || {};
    const nextChunkBottom = nextChunk.oldStart + nextChunk.oldLines;
    return contextLinesTop <= nextChunkBottom;
  }
}

function lineNumsBeforeTopicDiff(chunk: ChunkEntry, startingTo?: number) {
  const endingFrom = chunk.oldStart - 1;
  const endingTo = chunk.newStart - 1;

  return {
    endingTo,
    startingTo: startingTo || Math.max(endingTo - CONTEXT_LINES_RANGE, 1),
    startingFrom: startingTo
      ? endingFrom - (endingTo - startingTo)
      : Math.max(endingFrom - CONTEXT_LINES_RANGE, 1),
  };
}

function lineNumsAfterTopicDiff(chunk: ChunkEntry, endingTo?: number) {
  const from = chunk.oldStart + chunk.oldLines;
  const to = chunk.newStart + chunk.newLines;

  return {
    startingFrom: from,
    startingTo: to,
    endingTo: endingTo || to + CONTEXT_LINES_RANGE,
  };
}

export function getContextDataTopicDiff(
  chunks: ChunkEntry[],
  expanderIndex: number,
  isShiftClicked: boolean,
  shouldExpandDown: boolean
): ContextDatum {
  if (shouldExpandDown) {
    const chunkIndex = expanderIndex - 1;
    const chunk = chunks[chunkIndex];
    const isLastChunk = chunkIndex >= chunks.length - 1;

    let lineNums;

    if (isShiftClicked) {
      if (isLastChunk) {
        // If it's the last chunk, get the next 500 lines from the current chunk
        const endingTo =
          chunk.newStart + chunk.newLines + MULTI_CONTEXT_LINES_RANGE;
        lineNums = lineNumsAfterTopicDiff(chunk, endingTo);
      } else {
        const nextChunk = chunks[chunkIndex + 1];
        const endingTo = nextChunk.newStart - 1;
        const adjustedEndingTo =
          endingTo - (chunk.newStart + chunk.newLines) >
          MULTI_CONTEXT_LINES_RANGE
            ? chunk.newStart + chunk.newLines + MULTI_CONTEXT_LINES_RANGE
            : endingTo;

        lineNums = lineNumsAfterTopicDiff(chunk, adjustedEndingTo);
      }
    } else if (chunksWillConnect(chunks, chunkIndex, true)) {
      const nextChunk = chunks[chunkIndex + 1];
      lineNums = lineNumsAfterTopicDiff(chunk, nextChunk.newStart - 1);
    } else {
      lineNums = lineNumsAfterTopicDiff(chunk);
    }

    return {
      beforeOrAfter: 'after',
      chunkId: chunk.id,
      lineNums,
    };
  } else {
    const chunkIndex = expanderIndex;
    const chunk = chunks[chunkIndex];
    const isFirstChunk = chunkIndex === 0;

    let lineNums;

    if (isShiftClicked) {
      if (isFirstChunk) {
        // If it's the first chunk, get 500 lines before or start from line 1
        const startingTo = Math.max(
          chunk.newStart - MULTI_CONTEXT_LINES_RANGE,
          1
        );
        lineNums = lineNumsBeforeTopicDiff(chunk, startingTo);
      } else {
        const prevChunk = chunks[chunkIndex - 1];
        const startingTo = prevChunk.newStart + prevChunk.newLines;
        const adjustedStartingTo =
          chunk.newStart - startingTo > MULTI_CONTEXT_LINES_RANGE
            ? chunk.newStart - MULTI_CONTEXT_LINES_RANGE
            : startingTo;

        lineNums = lineNumsBeforeTopicDiff(chunk, adjustedStartingTo);
      }
    } else if (chunksWillConnect(chunks, chunkIndex, false)) {
      const prevChunk = chunks[chunkIndex - 1];

      lineNums = lineNumsBeforeTopicDiff(
        chunk,
        prevChunk.newStart + prevChunk.newLines
      );
    } else {
      lineNums = lineNumsBeforeTopicDiff(chunk);
    }

    return {
      beforeOrAfter: 'before',
      chunkId: chunk.id,
      lineNums,
    };
  }
}

/**
 * helper to parse context lines into array
 * The internal src api will return context lines in the format "line1\nline2\nline3\n"
 */
export function parseSrcApiLines(srcLines: string): string[] {
  // some files are not newline terminated (BCLOUD-22333)
  if (srcLines.slice(-1) === '\n') {
    return srcLines.split('\n').slice(0, -1);
  } else {
    return srcLines.split('\n');
  }
}
